import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { List, fromJS } from 'immutable';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import classNames from 'classnames';
import { push } from 'connected-react18-router';
import { createSelector } from 'reselect';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import copyToClipboard from '../../utils/copyToClipboard';
import { emitErrorTooltip } from '../../utils/errorTooltipEmitter';
import confirm from '../decorators/confirm';
import stopPropagation from '../../decorators/stopPropagation';
import { AddNewIndustryContext } from '../../contexts/AddNewIndustryContext';
import AutoCompleteContainer from '../AutoComplete';
import deletionCheck from '../services/deletionCheck';
import { isNew } from '../../utils/uniqueId';
import { preventDrop, allowDrop } from '../../utils/dropPrevent';
import endingRemoval from '../../utils/endingRemoval';
import { orderContactService } from '../services/contact';
import { unwrap, getChanged, isDeepChanged, toJS } from '../../utils/ChangeSpy';
import getOptionValue from '../../utils/getOptionValue';
import configureEmailMergedFields from '../../utils/configureEmailMergedFields';
import getContactMailLink from '../../utils/getContactMailLink';
import config from '../../config';
import {
  enterCompanyDealNote,
  enterCompanyEmail,
  leaveCompanyDealNote,
  leaveCompanyEmail,
} from '../../actions/company/companyEvents';
import * as companyTargetActions from '../../actions/company/companyTarget';
import { leadMemoReportRequest } from '../../actions/company/target/leadMemoReport';
import { updateDealNoteWidth } from '../../actions/auth';
import { reloadMainInfo } from '../../actions/companyDetail';
import {
  getTradeShows,
  loadSelectedTradeShow,
  removeTradeShow,
  saveCompanyContact,
  saveTradeShows,
  updateLastResearcher,
} from '../../actions/company/companyTarget';
import { fetchTargetStatuses } from '../../actions/statuses';
import { fetchEmailTemplates } from '../../actions/emailTemplates';
import { CompanyTarget } from '../../components/Company/CompanyTarget';
import { PrettyErrorProvider } from '../../contexts/PrettyErrorContext';
import { checkAccess, isResearcher, basicAccess } from '../../utils/checkPermissions';
import { showError, showInformationCustomTitle } from '../../utils/MessagePopup';
import { getLastEventBuyerText } from '../../helpers/getLastEventBuyerText';
import precheckDetail from './target/entitiesPreCheck';
import prepareDetail from './target/entitiesApiPrepape';
import { BUSINESS_MODELS, INDUSTRY_CATEGORIES, INDUSTRY_CATEGORY_ERROR } from '../../utils/industryTagsHelper';
import { usePopupContext } from '../../contexts/PopupContext';
import { useEntityEventContext } from '../../contexts/EntityEventContext';

const emptyList = List();

const normalizeField = data => {
  ['fyeMonth', 'dataQuality', 'corporationType'].forEach(field => {
    if (field in data) {
      data[field] = data[field] || null;
    }
  });

  return data;
};

const normalizeDate = date => {
  if (!date) return null;
  try {
    date = date.format('YYYY-MM-DD');
  } catch (error) {
    date = moment().format('YYYY-MM-DD');
  }

  return date;
};

const HOURS_LIMIT = 24;

const DETAIL_FIELD_NAMES = [
  'customFields',
  'harvcoTags',
  'industries',
  'targetCompetitors',
  'targetCustomers',
  'targetFacilities',
  'targetMarkets',
  'targetProducts',
  'tradeshowBuyers',
  'tradeShowTargets',
];

const getSuggestIdName = name => {
  switch (name) {
    case 'suggestDirector':
      return 'recordOwnerId';

    case 'suggestAnalyst':
      return 'recordSubOwnerId';

    case 'suggestDealMaker':
      return 'dealMakerId';

    case 'suggestResearcher':
      return 'lastResearcherId';

    case 'industryLabel':
      return 'pivot.industryId';

    case 'showName':
      return 'pivot.tradeshowId';

    default:
      return 'id';
  }
};

const getBuyerName = buyer => {
  if (!buyer) return null;

  return unwrap(buyer.get('dsplBuyerAbbrName')) ? buyer.get('dsplBuyerAbbrName') : buyer.get('dsplBuyerLegalName');
};

const getCompanyName = company =>
  unwrap(company.get('abbrName')) ? company.get('abbrName') : company.get('legalName');

const copyContact = (event, params) => {
  event.stopPropagation();
  copyToClipboard(params.value);
};

const CompanyTargetContainer = props => {
  const {
    activeBuyer,
    addDetailEntity,
    buyerType,
    changeBuyerApprove,
    checkValidity,
    closeFiscalError,
    closeValidationError,
    company,
    companyInfo,
    companyName,
    contacts,
    dealNotes,
    dealNoteSize,
    deleteBuyer,
    deleteContact,
    deleteContactChannel,
    deleteDetailEntity,
    deleteFiscalYear,
    deleteFile,
    emails,
    emailTemplates,
    enterCompanyDealNote,
    enterCompanyEmail,
    fetchEmailTemplates,
    fetchTargetStatuses,
    files,
    findApprovalLists,
    findDirectors,
    findProjects,
    findTradeShows,
    findUsers,
    getBuyers,
    getFiles,
    getSubsidiaries,
    getTradeShows,
    handleUpdateCompany,
    infoSectionHeight,
    inputErrors,
    isDuplicateCompany,
    lastEventBuyerText,
    leadMemoReportRequest,
    leaveCompanyDealNote,
    leaveCompanyEmail,
    loading,
    loadSelectedTradeShow,
    match,
    openBuyer,
    openContact,
    openedContact,
    push,
    reloadMainInfo,
    removeTradeShow,
    reorderChannels,
    reorderContacts,
    saveBuyers,
    saveCompanyContact,
    saveCompanyDetailEntity,
    saveFiscalYear,
    saveIndustryBoard,
    saveInfo,
    saveTradeShows,
    selectBusinessModels,
    startEditDetailEntity,
    statuses,
    updateDealNoteWidth,
    updateIndustryCategory,
    updateLastResearcher,
    uploadFile,
    user,
  } = props;

  const popupContext = usePopupContext();
  const { openPopup, closePopup } = popupContext;
  const {
    onEditEvent,
    addOnSaveCallback,
    removeOnSaveCallback,
    addValidateCallBacks,
    deleteValidateCallbacks,
  } = useEntityEventContext();

  const { companyId } = match.params;

  const [isClosed, setIsClosed] = useState(() => dealNotes.size === 0);
  const [industryCategory, setIndustryCategory] = useState('');
  const [businessModel, setBusinessModel] = useState('');
  const [showInstruction, setShowInstruction] = useState(false);
  const [hasPermissions, setHasPermissions] = useState(true);
  const [roleIsResearcher, setRoleIsResearcher] = useState(false);
  const [isBasicAccess, setIsBasicAccess] = useState(false);
  const [isUnapproved, setIsUnapproved] = useState(false);
  const [isOwnerResearcher, setIsOwnerResearcher] = useState(false);
  const [isLoadingLeadMemoReport, setIsLoadingLeadMemoReport] = useState(false);

  // Not tied to any prop/state comparison, matches the original's own
  // `this.didCheckTargetRole`/`this.didCheckOpenState` instance flags exactly -
  // genuine cross-render mutable flags, not state (setting them must never re-render).
  const didCheckTargetRoleRef = useRef(false);
  const didCheckOpenStateRef = useRef(false);
  const hasMountedRef = useRef(false);

  // Bundles every value the mount-once `validate`/`handleSubmit` callbacks (registered
  // below) read live, since a [] effect only ever sees the closures that existed at
  // mount - see [[feedback-minimize-useref]].
  const liveRef = useRef();

  const handleChange = useCallback(
    ({ name, value, summary }) => {
      if (name === 'dealNotes') {
        updateDealNoteWidth({ userId: user.get('id'), width: value.size.width });
      }

      if (summary) {
        handleUpdateCompany({ name, value, summary });
      } else {
        handleUpdateCompany({ name, value });
      }
    },
    [user, updateDealNoteWidth, handleUpdateCompany],
  );

  const handleSuggestChange = useCallback(
    ({ name, value }) => {
      handleChange({ name, value });
    },
    [handleChange],
  );

  const handleFormChange = useCallback(
    ({ target: { name, value } }) => {
      if (!roleIsResearcher && isUnapproved) return;

      const buyer = company.getIn(['buyers', 0]);
      const currentStatus = buyer ? buyer.get('currentStatus') : undefined;
      const newValue = roleIsResearcher && name === 'lastResearchedDate' ? moment() : value;

      if (currentStatus && name === 'buyers.0.currentStatus') {
        const currentValue = parseFloat(unwrap(currentStatus));
        const newStatus = parseFloat(newValue);

        if (currentValue === 4 && newStatus >= 2 && newStatus <= 3.9) {
          openPopup('StatusCodePopup', {
            start: unwrap(currentStatus),
            end: newValue,
            companyId,
            buyerId: buyer.get('id'),
          });
        }
      }

      handleChange({
        name,
        value: newValue,
        summary: statuses.getIn([statuses.findKey(status => status.get('value') === newValue), 'name']),
      });
    },
    [roleIsResearcher, isUnapproved, company, companyId, openPopup, statuses, handleChange],
  );

  const handleFiscalYearChange = useCallback(
    event => {
      if (!roleIsResearcher && isUnapproved) return;
      event.stopPropagation();

      const { name, value } = event.target;
      const id = parseInt(event.target.getAttribute('data-id'), 10);
      const index = company.get('targetFiscalYears').findIndex(target => target.get('id') === id);

      handleChange({ name: `targetFiscalYears.${index}.${name}`, value: value.replace(/^\$/, '') });
    },
    [roleIsResearcher, isUnapproved, company, handleChange],
  );

  const handleDetailChange = useCallback(
    event => {
      const { name, value } = event.target;

      handleChange({ name, value });
    },
    [handleChange],
  );

  const handleFiscalErrorClose = useCallback(
    (event, name, index) => {
      closeFiscalError(name, index);
    },
    [closeFiscalError],
  );

  const handleDetailSubmitItem = useCallback(
    detailName => {
      const base = company.get(detailName);
      const precheck = precheckDetail(detailName);

      if (!isDeepChanged(base)) return true;
      if (!company.get(`${detailName}IsValid`, true)) {
        emitErrorTooltip(inputErrors.toJS());

        return true;
      }

      const body = toJS(base);

      if (!precheck(body)) {
        checkValidity();

        return true;
      }
      body.forEach(entity => {
        Object.keys(entity).forEach(key => {
          if (entity[key] instanceof moment) {
            try {
              entity[key] = entity[key].format('YYYY-MM-DD');
            } catch (error) {
              console.error("Can't transform ", key, entity[key], error);
            }
          }
        });
      });

      let realBody = null;

      try {
        realBody = prepareDetail(detailName, body, user.get('id'));
      } catch (error) {
        return true;
      }

      saveCompanyDetailEntity({ companyId, name: detailName, body, realBody });
    },
    [company, inputErrors, checkValidity, user, companyId, saveCompanyDetailEntity],
  );

  const handleDetailSubmit = useCallback(() => {
    DETAIL_FIELD_NAMES.forEach(fieldName => {
      handleDetailSubmitItem(fieldName);
    });
  }, [handleDetailSubmitItem]);

  const handleBoardIndustrySubmit = useCallback(() => {
    const industryCategoryIds = unwrap(company.get(INDUSTRY_CATEGORIES))
      .map(tag => tag.get('id'))
      .toJS();
    const businessModelIds = unwrap(company.get(BUSINESS_MODELS))
      .map(tag => tag.get('id'))
      .toJS();

    saveIndustryBoard({
      industryCategoryIds: industryCategoryIds.concat(businessModelIds),
      companyId,
      afterSuccess: data => {
        handleUpdateCompany({ name: 'suggestResearcher', value: data.response.data.researchUser.userName });
      },
    });
  }, [company, saveIndustryBoard, companyId, handleUpdateCompany]);

  const getCompanyErrors = useCallback(() => {
    const errors = company.get('errors').toJS();

    return Object.keys(errors).reduce((accumulator, key) => {
      accumulator.push(company.getIn(['errors', key]).trim());

      return accumulator;
    }, []);
  }, [company]);

  const handleBuyerBlur = useCallback(() => {
    if (!roleIsResearcher && isUnapproved) return;
    if (company.get('isValid')) {
      const buyersToSave = company
        .get('buyers')
        .map((buyer, index) => buyer.set('buyerIndex', index))
        .filter(buyer => isDeepChanged(buyer));

      buyersToSave.forEach(buyer => {
        const body = getChanged(buyer);

        if (body.activeBuyer === false) delete body.activeBuyer;
        if (!Object.keys(body).length) return;

        const buyerId = buyer.get('buyerId');
        const id = buyer.get('id');
        const buyerIndex = buyer.get('buyerIndex');

        saveBuyers({ companyId, buyerIndex, buyerId, id, body });

        if (isBasicAccess) {
          updateLastResearcher(user.get('userName'));
        }
      });
    }
  }, [roleIsResearcher, isUnapproved, company, saveBuyers, companyId, isBasicAccess, updateLastResearcher, user]);

  const handleFiscalBlur = useCallback(() => {
    if (!roleIsResearcher && isUnapproved) return;

    company
      .get('targetFiscalYears')
      .filter(year => isDeepChanged(year))
      .forEach(year => {
        const body = getChanged(year);
        const yearId = year.get('id');

        body.fiscalYear = unwrap(year.get('fiscalYear'));

        saveFiscalYear({ companyId, yearId, body });
      });
  }, [roleIsResearcher, isUnapproved, company, companyId, saveFiscalYear]);

  const saveContact = useCallback(() => {
    const contact = contacts.get(openedContact);

    if (contact) {
      const contactId = contact.get('id');
      const saved = getChanged(contact);
      const { pivot = {} } = saved;

      saved.fullName = unwrap(contact.get('fullName'));
      saved.firstName = unwrap(contact.get('firstName'));
      saved.lastName = unwrap(contact.get('lastName'));
      saved.nick = unwrap(contact.get('nick'));
      saved.pronounce = unwrap(contact.get('pronounce'));

      const prefix = getOptionValue(contact.get('prefix'));
      const suffix = getOptionValue(contact.get('suffix'));
      const body = { ...pivot, ...saved, prefix, suffix };

      saveCompanyContact({ companyId, contactId, body, saved }, () => {});
    }
  }, [contacts, openedContact, saveCompanyContact, companyId]);

  liveRef.current = {
    company,
    match,
    contacts,
    user,
    roleIsResearcher,
    isUnapproved,
    isBasicAccess,
    openPopup,
    getCompanyErrors,
    handleDetailSubmit,
    handleBoardIndustrySubmit,
    handleBuyerBlur,
    handleFiscalBlur,
    saveContact,
    handleChange,
  };

  const handleSubmit = useCallback(event => {
    event.preventDefault();

    if (!liveRef.current.roleIsResearcher && liveRef.current.isUnapproved) return;

    const {
      company: currentCompany,
      match: currentMatch,
      contacts: currentContacts,
      user: currentUser,
    } = liveRef.current;

    if (!currentCompany.get('isValid')) {
      const errorMessage = liveRef.current.getCompanyErrors();

      showError(liveRef.current.openPopup, errorMessage);

      return;
    }

    const body = getChanged(currentCompany);
    const { companyId: currentCompanyId } = currentMatch.params;
    let afterSuccess = null;

    delete body.targetFiscalYears;
    delete body.businessModels;
    delete body.industryCategories;

    if ('lastResearchedDate' in body) body.lastResearchedDate = normalizeDate(body.lastResearchedDate);
    if ('balanceSheetDate' in body) body.balanceSheetDate = normalizeDate(body.balanceSheetDate);

    if ('description' in body || 'researchNotes' in body) {
      afterSuccess = () => {
        reloadMainInfo(currentCompanyId);
      };
    }

    const currentStatus = Number(currentCompany.getIn(['buyers', 0, 'currentStatus', 'value']));
    const isBasicAccessL1 = currentUser.get('roles').some(role => role.get('name') === 'BasicAccessL1');

    liveRef.current.handleDetailSubmit();
    liveRef.current.handleBoardIndustrySubmit();

    if (isBasicAccessL1 && currentStatus >= 2) {
      updateLastResearcher(currentUser.get('userName'));
      setIndustryCategory('');
      setBusinessModel('');

      return;
    }

    if (Object.keys(body).length) {
      if ('tradeShowTargetsSpyList' in body) {
        saveTradeShows(toJS(currentCompany.get('tradeShowTargetsSpyList')), currentCompanyId);
      }
      saveInfo({ companyId: currentCompanyId, body: normalizeField(body), afterSuccess });
    }

    liveRef.current.handleBuyerBlur();
    liveRef.current.handleFiscalBlur();

    if (isDeepChanged(currentContacts)) {
      liveRef.current.saveContact();
    }

    if (liveRef.current.isBasicAccess) {
      updateLastResearcher(currentUser.get('userName'));
    }
    setIndustryCategory('');
    setBusinessModel('');
  }, []);

  const validate = useCallback(() => {
    let isValid = true;
    const { company: currentCompany } = liveRef.current;
    const industryCategories = unwrap(currentCompany.get(INDUSTRY_CATEGORIES, emptyList));
    const businessModels = unwrap(currentCompany.get(BUSINESS_MODELS, emptyList));

    if (!businessModels.size) {
      setBusinessModel('Business model is required');
      isValid = false;
    }

    if (!industryCategories.size) {
      setIndustryCategory(INDUSTRY_CATEGORY_ERROR);
      isValid = false;
    }

    return isValid;
  }, []);

  const addNewIndustry = useCallback(
    text => {
      openPopup('NewIndustryPopup', {
        afterClose: value => {
          const index = liveRef.current.company.getIn(['industries']).findIndex(industry => industry.get('isEditing'));

          liveRef.current.handleChange({ name: `industries.${index}.industryLabel`, value: value.label });
          liveRef.current.handleChange({ name: `industries.${index}.id`, value: value.id });
          liveRef.current.handleChange({ name: `industries.${index}.pivot.industryId`, value: value.id });
          liveRef.current.handleDetailSubmit();
        },
        initialText: text,
      });
    },
    [openPopup],
  );

  const deleteContactAfterConfirm = useCallback(
    (event, contact) => {
      if (!roleIsResearcher && isUnapproved) return;

      const contactId = contact.id;
      const { entityContactsId } = contact;

      deleteContact({ contactId, entityContactsId, companyId }, closePopup);
    },
    [roleIsResearcher, isUnapproved, companyId, deleteContact, closePopup],
  );

  const handleContactDelete = useCallback(stopPropagation(confirm('Delete contact?', deleteContactAfterConfirm, popupContext)), [
    deleteContactAfterConfirm,
    popupContext,
  ]);

  const deleteContactChannelAfterConfirm = useCallback(
    (event, channelType, channelIndex) => {
      if (!roleIsResearcher && isUnapproved) return;

      const contactId = contacts.getIn([openedContact, 'id']);
      const channelId = contacts.getIn([openedContact, channelType, channelIndex, 'id']);

      deleteContactChannel({ companyId, contactId, type: channelType, channelId }, () => {
        closePopup();
        if (isBasicAccess) {
          updateLastResearcher(user.get('userName'));
        }
      });
    },
    [
      roleIsResearcher,
      isUnapproved,
      isBasicAccess,
      contacts,
      openedContact,
      companyId,
      deleteContactChannel,
      closePopup,
      updateLastResearcher,
      user,
    ],
  );

  const handleContactChannelDelete = useCallback(
    stopPropagation(confirm('Delete this field from contact?', deleteContactChannelAfterConfirm, popupContext)),
    [deleteContactChannelAfterConfirm, popupContext],
  );

  const editContact = useCallback(
    event => {
      if (!roleIsResearcher && isUnapproved) return;

      const { name, value } = event.target;

      if (name === 'notes') {
        handleUpdateCompany({ name: `contacts.${openedContact}.pivot.notes`, value });
      } else {
        openPopup('EditContactPopup', { openedContact, companyId });
      }
    },
    [roleIsResearcher, isUnapproved, handleUpdateCompany, openedContact, openPopup, companyId],
  );

  const addContact = useCallback(() => {
    if (!roleIsResearcher && isUnapproved) return;
    openPopup('AddCompanyContactPopup');
  }, [roleIsResearcher, isUnapproved, openPopup]);

  const editContactChannel = useCallback(
    (channelType, channelIndex) => {
      if (!roleIsResearcher && isUnapproved) return;

      const contactId = contacts.getIn([openedContact, 'id']);
      const channelId = contacts.getIn([openedContact, channelType, channelIndex, 'id']);
      const popupProps = { params: match.params, contactId, channelIndex, openedContact, channelId, companyInfo };

      if (channelType === 'phones') {
        openPopup('EditPhoneContactChannelPopup', popupProps);
      } else {
        openPopup('EditEmailContactChannelPopup', popupProps);
      }
    },
    [roleIsResearcher, isUnapproved, contacts, openedContact, match, companyInfo, openPopup],
  );

  const deleteFileAfterConfirm = useCallback(
    (event, file) => {
      deleteFile({ id: file.get('id'), companyId: file.get('companyId') }, () => {
        closePopup();
        getFiles({ companyId });
      });
    },
    [deleteFile, closePopup, getFiles, companyId],
  );

  const handleDeleteFile = useCallback(stopPropagation(confirm('Delete file?', deleteFileAfterConfirm, popupContext)), [
    deleteFileAfterConfirm,
    popupContext,
  ]);

  const handleDialNotesMouseEnter = useCallback(
    (event, eventId) => {
      enterCompanyDealNote({ eventId });
    },
    [enterCompanyDealNote],
  );

  const handleDialNotesMouseLeave = useCallback(
    (event, eventId) => {
      leaveCompanyDealNote({ eventId });
    },
    [leaveCompanyDealNote],
  );

  const handleEmailMouseEnter = useCallback(
    (event, eventId) => {
      enterCompanyEmail({ eventId });
    },
    [enterCompanyEmail],
  );

  const handleEmailMouseLeave = useCallback(
    (event, eventId) => {
      leaveCompanyEmail({ eventId });
    },
    [leaveCompanyEmail],
  );

  const handleAfterRequestSuccess = useCallback(
    ({ response }) => {
      const { fileName, fileUrl, errors } = response.data;
      const message = [{ name: fileName, url: fileUrl, headers: {} }];

      if (Array.isArray(errors)) {
        showError(openPopup, [errors[0]]);
      } else {
        showInformationCustomTitle(openPopup, 'The report has been generated successfully', message);
      }
      setIsLoadingLeadMemoReport(false);
    },
    [openPopup],
  );

  const handleAfterRequestError = useCallback(
    ({ responseBody }) => {
      showError(openPopup, [responseBody?.message || 'An error occurred']);
      setIsLoadingLeadMemoReport(false);
    },
    [openPopup],
  );

  const handleLeadMemoReport = useCallback(
    targetCompanyId => {
      setIsLoadingLeadMemoReport(true);
      leadMemoReportRequest({ companyId: targetCompanyId }, handleAfterRequestSuccess, handleAfterRequestError);
    },
    [leadMemoReportRequest, handleAfterRequestSuccess, handleAfterRequestError],
  );

  const handleContactOpen = useCallback(
    (event, index) => {
      openContact({ index });
    },
    [openContact],
  );

  const handleContactsReorder = useCallback(
    contactsList => {
      const sendToApi = contactsList
        .map(contact => ({ id: contact.get('id'), entityContactsId: contact.get('entityContactsId') }))
        .toJS();

      reorderContacts(contactsList, companyId, sendToApi);
    },
    [companyId, reorderContacts],
  );

  const handleChannelsReorder = useCallback(
    contactsList => {
      reorderChannels(contactsList, openedContact);
    },
    [reorderChannels, openedContact],
  );

  const handleUpdateTags = useCallback(() => {
    const tags = unwrap(company.get(INDUSTRY_CATEGORIES));
    const models = unwrap(company.get(BUSINESS_MODELS));

    openPopup('TagsManagementPopup', {
      addNew: false,
      clientView: true,
      selectedTags: tags.reduce((accumulator, tag) => {
        accumulator[tag.get('id')] = true;

        return accumulator;
      }, {}),
      selectedModels: models.reduce((accumulator, model) => {
        accumulator[model.get('id')] = true;

        return accumulator;
      }, {}),
      callback: (tagSelecteds, modelSelecteds) => {
        updateIndustryCategory(tagSelecteds);
        selectBusinessModels(modelSelecteds);
      },
    });
  }, [company, openPopup, updateIndustryCategory, selectBusinessModels]);

  const handleUpdateHighStatus = useCallback(
    buyer => {
      const updatedAt = buyer.getIn(['highStatusUpdated']);

      if (updatedAt) {
        const statusUpdatedHoursPassed = moment().diff(updatedAt, 'hours');

        openPopup('UpdateHighStatusPopup', {
          statuses,
          highStatus: unwrap(buyer.getIn(['highStatus'])),
          currentStatus: unwrap(buyer.getIn(['currentStatus'])),
          highStatusUpdateAt: moment(updatedAt),
          companyId,
          buyerId: buyer.get('id'),
          directorEmail: company.get('recordOwnerEmail'),
          isStatusExpired: statusUpdatedHoursPassed > HOURS_LIMIT,
        });
      }
    },
    [statuses, companyId, company, openPopup],
  );

  const handleInstructionClick = useCallback(() => {
    openPopup('DescriptionChangePopup');
  }, [openPopup]);

  const updateDescription = useCallback(
    value => {
      if (!value) return;

      let showInstructionNext = false;
      const words = value.trim().split(' ');
      const first = words[0].toLowerCase();
      let last = words[words.length - 1];

      if (['a', 'an', 'the'].indexOf(first) > -1) {
        words[0] = first;
      } else {
        showInstructionNext = true;
      }

      if (words.length > 1) {
        last = endingRemoval(['.', '!', '?'], last);
        words[words.length - 1] = last;
      }

      if (
        [
          ',',
          ';',
          ':',
          '"',
          "'",
          '~',
          '`',
          '@',
          '#',
          '$',
          '%',
          '^',
          '&',
          '*',
          '(',
          ')',
          '-',
          '_',
          '+',
          '=',
          '|',
          '\\',
          '/',
          '<',
          '>',
          '{',
          '}',
          '[',
          ']',
        ].indexOf(last[last.length - 1]) > -1
      ) {
        showInstructionNext = true;
      }

      handleUpdateCompany({ name: 'description', value: words.join(' ') });
      setShowInstruction(showInstructionNext);
    },
    [handleUpdateCompany],
  );

  const handleDescriptionBlur = useCallback(
    event => {
      updateDescription(event.target.value);
    },
    [updateDescription],
  );

  const handleFileUpload = useCallback(
    event => {
      const { currentTarget } = event;
      const formData = new FormData(currentTarget);

      event.preventDefault();

      const afterSuccess = () => currentTarget.reset();

      uploadFile({ companyId, formData, afterSuccess });
    },
    [companyId, uploadFile],
  );

  const handleBuyerChangeApprove = useCallback(
    (event, { target }, index) => {
      if (isDuplicateCompany) return;
      if (!roleIsResearcher && isUnapproved) return;

      changeBuyerApprove({ target, index });
    },
    [isDuplicateCompany, roleIsResearcher, isUnapproved, changeBuyerApprove],
  );

  const handleBuyerClick = useCallback(
    (event, index) => {
      openBuyer({ index });
    },
    [openBuyer],
  );

  const handleBuyerActivate = useCallback(
    (event, index) => {
      if (isDuplicateCompany) return;
      activeBuyer({ index });
    },
    [isDuplicateCompany, activeBuyer],
  );

  const handleBuyerAdd = useCallback(() => {
    if (isDuplicateCompany) return;
    if (!roleIsResearcher && isUnapproved) return;

    openPopup('AddCompanyBuyerPopup', { companyId });
  }, [isDuplicateCompany, roleIsResearcher, isUnapproved, openPopup, companyId]);

  const deleteBuyerAfterConfirm = useCallback(
    (event, data) => {
      if (!roleIsResearcher && isUnapproved) return;

      handleBuyerBlur();
      setTimeout(() => {
        const id = (data && data.id) || 0;

        deleteBuyer({ id, companyId }, closePopup);
      }, 50);
    },
    [roleIsResearcher, isUnapproved, handleBuyerBlur, deleteBuyer, companyId, closePopup],
  );

  const handleBuyerDelete = useCallback(
    stopPropagation(deletionCheck(user, confirm('Delete this buyer?', deleteBuyerAfterConfirm, popupContext), popupContext)),
    [user, deleteBuyerAfterConfirm, popupContext],
  );

  const handleFiscalEdit = useCallback(
    (event, data) => {
      if (!roleIsResearcher && isUnapproved) return;

      const yearId = (data && data.id) || 'new';

      openPopup('EditFiscalYearPopup', { companyId, yearId });
    },
    [roleIsResearcher, isUnapproved, companyId, openPopup],
  );
  const handleFiscalAdd = handleFiscalEdit;

  const deleteFiscalYearAfterConfirm = useCallback(
    (event, data) => {
      if (!roleIsResearcher && isUnapproved) return;

      const yearId = (data && data.id) || 0;

      deleteFiscalYear({ yearId, companyId }, closePopup);
    },
    [roleIsResearcher, isUnapproved, deleteFiscalYear, companyId, closePopup],
  );

  const handleFiscalYearDelete = useCallback(
    stopPropagation(confirm('Delete this year?', deleteFiscalYearAfterConfirm, popupContext)),
    [deleteFiscalYearAfterConfirm, popupContext],
  );

  const handleDetailAdd = useCallback(
    (event, detailName) => {
      addDetailEntity({ name: detailName });
    },
    [addDetailEntity],
  );

  const deleteDetailAfterConfirm = useCallback(
    (event, detailName, data, menu, name) => {
      const id = unwrap(data.id);
      const entities = company.get(detailName);
      const body = toJS(entities)
        .filter(entity => entity.id !== id)
        .map(entity => {
          Object.keys(entity).forEach(key => {
            if (entity[key] instanceof moment) {
              try {
                entity[key] = entity[key].format('YYYY-MM-DD');
              } catch (error) {
                console.error("Can't transform ", key, entity[key], error);
              }
            }
          });

          return entity;
        });

      deleteDetailEntity(
        {
          name: detailName,
          errorKey: name,
          id,
          companyId,
          body,
          realBody: isNew(id) ? null : prepareDetail(detailName, body, user.get('id')),
        },
        () => {
          closePopup();
        },
      );
    },
    [company, companyId, deleteDetailEntity, user, closePopup],
  );

  const handleDetailDelete = useCallback(
    stopPropagation(confirm('Delete this?', deleteDetailAfterConfirm, popupContext)),
    [deleteDetailAfterConfirm, popupContext],
  );

  const handleDetailEdit = useCallback(
    (field, detailName, detailIndex) => {
      startEditDetailEntity({ field, name: detailName, index: detailIndex });
    },
    [startEditDetailEntity],
  );

  const handleErrorClose = useCallback(
    (event, field) => {
      if (field === INDUSTRY_CATEGORIES) {
        setIndustryCategory('');
        setBusinessModel('');
      }
      closeValidationError({ field });
    },
    [closeValidationError],
  );

  const handleToggle = useCallback(() => {
    didCheckOpenStateRef.current = true;
    setIsClosed(previous => !previous);
  }, []);

  const handleTradeShowDelete = useCallback(
    (event, detailName, data, menu, name, index) => {
      removeTradeShow(index);
    },
    [removeTradeShow],
  );

  const handleTradeShowSelect = useCallback(
    suggestion => {
      loadSelectedTradeShow(suggestion.id);
    },
    [loadSelectedTradeShow],
  );

  const getTradeShowsSuggest = useCallback(
    (detailField, index, name, columnIndex, addNew, value) => {
      const prefix = `${detailField}.${index}.`;
      const suggestionsName = 'tradeShowsSuggests';
      const currentRowData = toJS(company.get('tradeShowTargets'))[index];

      if ('event' in currentRowData) {
        return <span className="min-sizes">{value}</span>;
      }

      return (
        <AutoCompleteContainer
          addNew={addNew}
          idName={prefix + getSuggestIdName(name)}
          idValue={company.getIn([detailField, index, getSuggestIdName(name)])}
          inputProps={{ autoFocus: columnIndex === 0, name: prefix + name }}
          onChange={handleSuggestChange}
          onFind={params => {
            findTradeShows({ ...params, companyId });
          }}
          onSelectCallback={handleTradeShowSelect}
          rootPath={['targetCompany', 'target']}
          suggestions={company.get(suggestionsName)}
          suggestionsName={suggestionsName}
          suggestionsPath={prefix + suggestionsName}
          valueName={prefix + name}
        />
      );
    },
    [company, findTradeShows, companyId, handleSuggestChange, handleTradeShowSelect],
  );

  const getSuggest = name => {
    const find = name === 'suggestDirector' ? findDirectors : findUsers;

    return (
      <AutoCompleteContainer
        idName={getSuggestIdName(name)}
        idValue={company.get(getSuggestIdName(name))}
        onChange={handleSuggestChange}
        onFind={find}
        suggestions={company.get('suggests')}
        suggestionsName="suggests"
        value={company.get(name)}
        valueName={name}
      />
    );
  };

  const buyers = company.get('buyers');

  const buyersSuggests = useMemo(() => {
    let activeBuyerIndex = 0;

    buyers.some((buyer, index) => {
      if (buyer.get('isOpened')) {
        activeBuyerIndex = index;

        return true;
      }

      return false;
    });

    const prefix = `buyers.${activeBuyerIndex}.`;
    const buyerId = unwrap(company.getIn(`${prefix}buyerId`.split('.')));
    const projectId = unwrap(company.getIn(`${prefix}projectId`.split('.')));

    return {
      suggestApprovalList: (
        <AutoCompleteContainer
          idName={`${prefix}approvalListId`}
          inputProps={{ name: `${prefix}dsplApplistLabel`, disabled: !projectId }}
          onChange={handleSuggestChange}
          onFind={options => {
            findApprovalLists({
              ...options,
              field: `${prefix}dsplApplistLabel`,
              buyerIndex: activeBuyerIndex,
              buyerId,
              projectId,
            });
          }}
          rootPath={['targetCompany', 'target']}
          suggestionsName={`${prefix}approveSuggests`}
          valueName={`${prefix}dsplApplistLabel`}
        />
      ),
      suggestProject: (
        <AutoCompleteContainer
          idName={`${prefix}projectId`}
          inputProps={{ name: `${prefix}dsplProjectCategory` }}
          onChange={handleSuggestChange}
          onFind={options => {
            findProjects({ ...options, field: `${prefix}dsplProjectCategory`, buyerIndex: activeBuyerIndex, buyerId });
          }}
          render={suggestion => {
            const className = classNames({ 'text-muted': !suggestion.isActive });

            return <span className={className}>{suggestion.text}</span>;
          }}
          rootPath={['targetCompany', 'target']}
          suggestionsName={`${prefix}projectSuggests`}
          valueName={`${prefix}dsplProjectCategory`}
        />
      ),
    };
  }, [buyers, company, handleSuggestChange, findApprovalLists, findProjects]);

  const details = useMemo(() => company.filter((value, key) => DETAIL_FIELD_NAMES.indexOf(key) !== -1), [
    company.get('customFields'),
    company.get('harvcoTags'),
    company.get('industries'),
    company.get('targetCompetitors'),
    company.get('targetCustomers'),
    company.get('targetFacilities'),
    company.get('targetMarkets'),
    company.get('targetProducts'),
    company.get('tradeshowBuyers'),
    company.get('tradeShowTargets'),
    // Also watched (but not part of the filtered result) to match the original's
    // UNSAFE_componentWillReceiveProps invalidation list exactly - a harmless
    // over-invalidation quirk in the original, preserved rather than "fixed".
    company.get('targetFiscalYears'),
  ]);

  const detailsCallbacks = useMemo(
    () => ({
      onAdd: handleDetailAdd,
      onDelete: handleDetailDelete,
      onChange: handleDetailChange,
      onClick: handleDetailEdit,
      onTradeShowDelete: handleTradeShowDelete,
    }),
    [handleDetailAdd, handleDetailDelete, handleDetailChange, handleDetailEdit, handleTradeShowDelete],
  );

  const getEmailMergedFields = useCallback(() => {
    const contact = companyInfo.getIn(['entityContacts', 0]);
    const products = details.get('targetProducts');
    const markets = details.get('targetMarkets');
    const activeBuyerForEmail = company.get('buyers').find(buyer => unwrap(buyer.get('activeBuyer')));

    return configureEmailMergedFields(contact, contacts, activeBuyerForEmail, companyInfo, markets, products, company);
  }, [companyInfo, details, company, contacts]);

  const handleOpenEmail = useCallback(
    (email, template) => {
      const mergedFields = getEmailMergedFields();
      const contact = contacts.get(openedContact);
      const mailLink = getContactMailLink(email, template, mergedFields, user, contact);

      window.open(mailLink, '_blank');
    },
    [getEmailMergedFields, contacts, openedContact, user],
  );

  const addNewIndustryContextValue = useMemo(() => ({ addNewIndustry, onFiscalErrorClose: handleFiscalErrorClose }), [
    addNewIndustry,
    handleFiscalErrorClose,
  ]);

  useEffect(() => {
    setHasPermissions(checkAccess(user.getIn(['roles', 0])));
    setRoleIsResearcher(isResearcher(user.getIn(['roles', 0, 'slug'])));
    setIsBasicAccess(basicAccess(user.getIn(['roles', 0, 'slug'])));
    addValidateCallBacks(validate);
    addOnSaveCallback(handleSubmit);
    getFiles({ companyId });
    getSubsidiaries({ companyId });
    getBuyers({ companyId });
    getTradeShows(companyId);
    fetchTargetStatuses();
    preventDrop();

    return () => {
      deleteValidateCallbacks(validate);
      removeOnSaveCallback(handleSubmit);
      allowDrop();
    };
  }, []);

  // Mirrors the original componentDidUpdate exactly: it has NO prevProps comparison
  // gate at all, so it re-runs after EVERY update (never on mount). It also mirrors
  // the original's early `return` when redirecting away, which skips the deal-notes
  // and isUnapproved/isOwnerResearcher checks for that one update cycle.
  useEffect(() => {
    if (hasMountedRef.current) {
      if (!didCheckTargetRoleRef.current) {
        const isTarget = company.get('isTarget', false);

        if (loading === false) {
          if (buyerType === 'financial' || isTarget === false) {
            push(`/company/${companyId}/buyer`);

            return;
          }
          didCheckTargetRoleRef.current = true;
        }
      }

      if (!didCheckOpenStateRef.current) {
        if (dealNotes.size > 0) {
          setIsClosed(false);
          didCheckOpenStateRef.current = true;
        }
      }

      const nextIsUnapproved = !companyInfo.get('isApproved');
      const researcherId = companyInfo.get('createdResearcherId');
      const nextIsOwnerResearcher = researcherId === user.get('id');

      setIsUnapproved(nextIsUnapproved);
      setIsOwnerResearcher(nextIsOwnerResearcher);
    } else {
      hasMountedRef.current = true;
    }
  });

  if (!company.get('loaded')) return null;

  const firstBuyer = company.getIn(['buyers', 0]);

  const contactsProps = {
    reorderContacts: handleContactsReorder,
    reorderChannels: handleChannelsReorder,
    companyName,
    buyerName: getBuyerName(firstBuyer),
    addContact,
    deleteContact: handleContactDelete,
    opened: openedContact,
    openContact: handleContactOpen,
    contacts,
    onCopy: copyContact,
    onDelete: handleContactChannelDelete,
    onEdit: editContact,
    onOpenEmail: handleOpenEmail,
    onEditChannel: editContactChannel,
  };

  const buyersProps = {
    ...buyersSuggests,
    onClick: handleBuyerClick,
    onActivate: handleBuyerActivate,
    onDelete: handleBuyerDelete,
    onSubmit: handleBuyerBlur,
    onBlur: handleBuyerBlur,
    buyers,
    onChangeApprove: handleBuyerChangeApprove,
    addBuyer: handleBuyerAdd,
  };

  const financialsProps = {
    company,
    onChange: handleFormChange,
    onYearChange: handleFiscalYearChange,
    onTableSubmit: handleFiscalBlur,
    onSubmit: handleSubmit,
    onAdd: handleFiscalAdd,
    onDelete: handleFiscalYearDelete,
    onEdit: handleFiscalEdit,
  };

  const subsidiariesProps = {
    subsidiaries: company.get('subsidiaries'),
  };

  const suggestDirector = getSuggest('suggestDirector');
  const suggestAnalyst = getSuggest('suggestAnalyst');
  const suggestDealMaker = getSuggest('suggestDealMaker');
  const suggestResearcher = getSuggest('suggestResearcher');
  const detailsSuggests = company.get('suggests');
  const industryCategories = company.get(INDUSTRY_CATEGORIES);
  const businessModels = company.get(BUSINESS_MODELS);

  return (
    <AddNewIndustryContext.Provider value={addNewIndustryContextValue}>
      <PrettyErrorProvider inputErrors={inputErrors} onErrorClose={handleErrorClose}>
        <CompanyTarget
          businessModels={businessModels}
          buyersProps={buyersProps}
          companyId={companyId}
          companyInfo={company}
          contactsProps={contactsProps}
          dealNotes={dealNotes}
          dealNoteSize={dealNoteSize}
          details={details}
          detailsCallbacks={detailsCallbacks}
          detailsSuggests={detailsSuggests}
          emails={emails}
          emailTemplates={emailTemplates}
          error={{ industryCategory, businessModel }}
          fetchEmailTemplates={fetchEmailTemplates}
          files={files}
          financialsProps={financialsProps}
          findDirectors={findDirectors}
          hasPermissions={hasPermissions}
          industryCategories={industryCategories}
          infoSectionHeight={infoSectionHeight}
          isClosed={isClosed}
          isDuplicateCompany={isDuplicateCompany}
          isLoadingLeadMemoReport={isLoadingLeadMemoReport}
          isOwnerResearcher={isOwnerResearcher}
          isUnapproved={isUnapproved}
          lastEventBuyerText={lastEventBuyerText}
          onChange={handleFormChange}
          onDeleteFile={handleDeleteFile}
          onDescriptionBlur={handleDescriptionBlur}
          onEditEvent={onEditEvent}
          onEnterDealNotes={handleDialNotesMouseEnter}
          onEnterEmail={handleEmailMouseEnter}
          onErrorClose={handleErrorClose}
          onFileUpload={handleFileUpload}
          onInstructionClick={handleInstructionClick}
          onLeadMemoReport={handleLeadMemoReport}
          onLeaveDealNotes={handleDialNotesMouseLeave}
          onLeaveEmail={handleEmailMouseLeave}
          onSubmit={handleSubmit}
          onToggle={handleToggle}
          onUpdateHighStatus={handleUpdateHighStatus}
          onUpdateTags={handleUpdateTags}
          roleIsResearcher={roleIsResearcher}
          showInstruction={showInstruction}
          statuses={statuses}
          subsidiariesProps={subsidiariesProps}
          suggestAnalyst={suggestAnalyst}
          suggestDealMaker={suggestDealMaker}
          suggestDirector={suggestDirector}
          suggestResearcher={suggestResearcher}
          tradeShowSuggestCreate={getTradeShowsSuggest}
          user={user}
        />
      </PrettyErrorProvider>
    </AddNewIndustryContext.Provider>
  );
};

const companyEventsSelector = state => state.targetCompany.events.get('all');
const lastEventBuyerTextSelector = createSelector(
  companyEventsSelector,
  events => events && fromJS(getLastEventBuyerText({ data: { events: events.toJS() } })),
);

function mapStateToProps(state) {
  const company = state.targetCompany.target;
  const companyInfo = state.targetCompany.info.get('info');
  const buyerInfo = state.targetCompany.buyer.info;
  const dealNoteSize = {
    minConstraints: [config.noteBoxSize.get('min'), null],
    width: state.auth.getIn(['user', 'tdnwidth']) || config.noteBoxSize.get('default'),
  };

  return {
    lastEventBuyerText: lastEventBuyerTextSelector(state),
    companyName: getCompanyName(companyInfo),
    infoSectionHeight: companyInfo.get('height'),
    isDuplicateCompany: state.targetCompany.mergeInfo.get('isDuplicateCompany'),
    companyInfo,
    company,
    inputErrors: company.get('inputErrors'),
    files: company.get('files', emptyList),
    dealNotes: state.targetCompany.events.get('dealNotes', emptyList),
    dealNoteSize,
    user: state.auth.get('user'),
    emails: state.targetCompany.events.get('emails', emptyList),
    openedContact: company.get('openedContact', 0),
    contacts: company.get('contacts', emptyList),
    buyerType: buyerInfo.get('buyerType', '').toLowerCase(),
    loading: state.targetCompany.info.get('isFetching') || !buyerInfo.get('loaded'),
    statuses: fromJS(state.statuses.get('targetStatuses')),
    emailTemplates: state.emailTemplates.get('data', emptyList),
  };
}

CompanyTargetContainer.propTypes = {
  isDuplicateCompany: PropTypes.bool.isRequired,
};

function mapDispatchToProps(dispatch) {
  return {
    ...bindActionCreators(companyTargetActions, dispatch),
    ...bindActionCreators(
      {
        leadMemoReportRequest,
        enterCompanyDealNote,
        enterCompanyEmail,
        leaveCompanyDealNote,
        leaveCompanyEmail,
        saveCompanyContact,
        updateDealNoteWidth,
        reloadMainInfo,
        fetchTargetStatuses,
        fetchEmailTemplates,
        updateLastResearcher,
        getTradeShows,
        loadSelectedTradeShow,
        removeTradeShow,
        saveTradeShows,
      },
      dispatch,
    ),
    reorderChannels: orderContactService(dispatch, companyTargetActions.reorderChannels),
    push,
  };
}

export { CompanyTargetContainer };
export default connect(mapStateToProps, mapDispatchToProps, mergeProps, connectOptions)(memo(CompanyTargetContainer));
